'use client';
import { useEffect, useRef, useState } from 'react';
import setting from '@/utils/config.util';
import { getCookie } from '@/utils/cookies.util';
import { encryptAes256CbcOpenSsl } from '@/utils';

interface FetchOptions {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  params?: Record<string, string | number>;
  body?: unknown;
  showLoader?: boolean;
}

interface FetchResponse<T> {
  response: T | null;
  error: string;
  loading: boolean;
  fetchData: (
    options: FetchOptions,
  ) => Promise<T | { error: string } | undefined>;
}

const useFetch = <T = unknown>(initialLoading = false): FetchResponse<T> => {
  const [response, setResponse] = useState<T | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(initialLoading);

  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const generateEncryptedHeaders = (): Record<string, string> => {
    const token = getCookie('token') || '';
    const createdAt = new Date().getTime();
    const password = `${setting.APP_ENCRYPTION_KEY}${createdAt}`;
    const apiKey = encryptAes256CbcOpenSsl(password, setting.APP_ENCRYPTION_KEY);

    return {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Methods': 'GET, PUT, POST, DELETE, OPTIONS',
      appType: 'Customer',
      createdAt: createdAt.toString(),
      apikey: apiKey,
    };
  };

  const fetchData = async ({
    url,
    method = 'GET',
    headers = {},
    params = {},
    body = null,
    showLoader = true,
  }: FetchOptions): Promise<T | { error: string } | undefined> => {
    setLoading(showLoader);
    controllerRef.current = new AbortController();

    const query = new URLSearchParams(
      params as Record<string, string>,
    ).toString();
    const fullUrl = query ? `${url}?${query}` : url;

    const mergedHeaders = {
      ...generateEncryptedHeaders(),
      ...headers,
    };

    try {
      const res = await fetch(fullUrl, {
        method,
        headers: mergedHeaders,
        body: body ? JSON.stringify(body) : null,
        signal: controllerRef.current.signal,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || 'Request failed');
      }

      setResponse(data);
      return data;
    } catch (err: unknown) {
      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          console.warn('Fetch aborted:', err.message);
          return;
        }

        const message = err.message || 'Unknown error';
        setError(message);
        return { error: message };
      } else {
        const message = 'An unexpected error occurred';
        setError(message);
        return { error: message };
      }
    } finally {
      setLoading(false);
    }
  };

  return { response, error, loading, fetchData };
};

export default useFetch;
